import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";

export const CrisisExitPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkForRecentCrisisExit();
  }, []);

  const checkForRecentCrisisExit = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if there's a recent crisis exit in the last session
      const { data: recentExit } = await supabase
        .from('audit_logs')
        .select('created_at, details')
        .eq('user_id', user.id)
        .eq('action', 'crisis_mode_exit')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (recentExit) {
        const exitTime = new Date(recentExit.created_at);
        const hoursSinceExit = (Date.now() - exitTime.getTime()) / (1000 * 60 * 60);
        
        // Show prompt if exit was within last 24 hours and user hasn't dismissed it
        const details = recentExit.details as any;
        if (hoursSinceExit < 24 && details?.nextLoginPrompt) {
          setShowPrompt(true);
        }
      }
    } catch (error) {
      console.error('Error checking crisis exit:', error);
    }
  };

  const handleReviewSafetyPlan = () => {
    setShowPrompt(false);
    navigate('/crisis');
  };

  const handleDismiss = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Update the audit log to mark prompt as dismissed
      await supabase
        .from('audit_logs')
        .update({ 
          details: { 
            nextLoginPrompt: false,
            promptDismissed: true,
            dismissedAt: new Date().toISOString()
          }
        })
        .eq('user_id', user.id)
        .eq('action', 'crisis_mode_exit')
        .order('created_at', { ascending: false })
        .limit(1);

      setShowPrompt(false);
    } catch (error) {
      console.error('Error dismissing prompt:', error);
      setShowPrompt(false);
    }
  };

  return (
    <Dialog open={showPrompt} onOpenChange={setShowPrompt}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Heart className="h-6 w-6 text-primary" />
            <DialogTitle>Welcome back</DialogTitle>
          </div>
          <DialogDescription className="text-base pt-2">
            Would you like to review or update your Safety Plan and Hope Kit after last time? 
            Small reflections help keep you feeling grounded.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            onClick={handleReviewSafetyPlan}
            className="flex-1"
          >
            Review Safety Plan
          </Button>
          <Button
            onClick={handleDismiss}
            variant="outline"
            className="flex-1"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
